import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, getIn } from 'formik';
import * as Yup from 'yup';
import logo from '../logo-trans.png';
import { useBodyClass, useLangParam } from '../hooks';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../components/LanguageSelect';
import { ApiContext } from '../context/Api';
import MUI from '../mui'

function ForgotPassword() {
  useLangParam();
  const { apiService } = useContext(ApiContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  useBodyClass('forgot-password');
  useBodyClass('auth-screen');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('userInfo'));
    if (user) {
      navigate('/');
    }
  }, [navigate]);

  async function onReset(values) {
    setIsLoading(true);

    apiService
      .sendPostRequest('auth/forgot-password', {
        email: values.email,
        url: `${window.location.origin}/reset-password` //todo: this needs to be the url to reset password and verify the code sent to the user via email
      })
      .then(() => {
        // Handle success.
        setIsLoading(false);
        setSuccess('An email has been sent to you');
        console.log('Your user received an email');
      })
      .catch(error => {
        // Handle error.
        setError(error.response.data.message[0].messages[0].message);
        setIsLoading(false);
        console.log('An error occurred:', error.response);
      });
  }

  return (
    <MUI mode="dark">
      <Container>
        <div className="top-lang">
          <LanguageSelect showLanguage />
        </div>
        <Stack
          direction="column"
          useFlexGap
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            marginBlockEnd: '1rem',
          }}
        >
          <img
            alt=""
            src={logo}
            width="80"
            className="d-inline-block align-top logo"
          />
          <Typography variant="h4">
            {t('core.progressingTogether')}
          </Typography>
          <Typography variant="h5">
            {t('auth.forgotPassword')}
          </Typography>
        </Stack>
        <Formik
          initialValues={{ email: '' }}
          validationSchema={Yup.object({
            email: Yup.string()
              .email(t('core.invalidEmail'))
              .required(t('core.required'))
          })}
          onSubmit={(values, { setSubmitting }) => {
            setError(null);
            setSuccess(null);
            onReset(values);
            setSubmitting(false);
          }}
        >
          {({ handleChange, errors, touched }) => (
            <Form className="form-base">
              <Stack direction="column" useFlexGap={true} spacing={2}>
                <TextField
                  label={t('core.email') + '*'}
                  className="form-field"
                  type="text"
                  name="email"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'email') && getIn(errors, 'email')
                  )}
                  helperText={
                    getIn(touched, 'email') && getIn(errors, 'email')
                      ? getIn(errors, 'email')
                      : ''
                  }
                />

                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  color="dark"
                  size="large"
                  loading={isLoading}
                  sx={{ width: '100%', fontSize: '1.25rem' }}
                >
                  {t('auth.forgotPassword')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
        <div className="end-text text-center">
          {t('auth.alreadyHaveAccount') + ' '}
          <Link className="login-link-text" to="../signin">
            {t('auth.login')}
          </Link>
        </div>
      </Container>
    </MUI>
  );
}
export default ForgotPassword;
